/**
 * @spec docs/specs/features/wallet-relation-api.md#ui-requirements
 * Wallet address display component with optional copy-to-clipboard functionality
 */
import { forwardRef, useCallback, type HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { formatAddress } from '@/lib/format';
import { showToast } from '@/lib/toast';
import CopyIcon from '@/assets/icons/copy.svg?react';

export interface WalletAddressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The full wallet address */
  address: string;
  /** Number of characters to show on each side (default: 4) */
  chars?: number;
  /** Whether to show the copy icon and enable click-to-copy (default: false) */
  showCopy?: boolean;
  /** Whether to show the full address without truncation (default: false) */
  full?: boolean;
}

/**
 * WalletAddress displays a formatted wallet address with optional copy functionality
 *
 * Features:
 * - Displays truncated address in monospace font
 * - Optional copy icon that copies full address to clipboard on click
 * - Shows toast notification on successful copy
 */
const WalletAddress = forwardRef<HTMLDivElement, WalletAddressProps>(
  ({ address, chars = 4, showCopy = false, full = false, className, ...props }, ref) => {
    const handleCopy = useCallback(async () => {
      if (!showCopy) return;

      try {
        await navigator.clipboard.writeText(address);
        showToast.success('Address copied to clipboard');
      } catch {
        showToast.error('Failed to copy address');
      }
    }, [address, showCopy]);

    const formattedAddress = full ? address : formatAddress(address, chars);

    if (showCopy) {
      return (
        <div
          ref={ref}
          role="button"
          tabIndex={0}
          onClick={() => void handleCopy()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              void handleCopy();
            }
          }}
          className={cn(
            'inline-flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity',
            className
          )}
          title={`Copy ${address}`}
          {...props}
        >
          <span className="font-mono text-sm">{formattedAddress}</span>
          <CopyIcon className="w-4 h-4 text-text-muted" aria-hidden="true" />
        </div>
      );
    }

    return (
      <span ref={ref} className={cn('font-mono text-sm', className)} {...props}>
        {formattedAddress}
      </span>
    );
  }
);

WalletAddress.displayName = 'WalletAddress';

export { WalletAddress };
